import React from "react";
import { COUNTDOWN_OPTIONS } from "../constants/captureSettings";
import { chip, chipActive } from "../styles/ui";

const SHOT_MODES = [
    { id: "auto", label: "Tự động cả dải" },
    { id: "manual", label: "Bấm từng tấm" }
];

// Bảng cài đặt chụp, thu gọn mặc định để người dùng lần đầu không bị ngợp.
//
// Chỉ hiển thị những tuỳ chọn ĐÃ chạy được. Các tuỳ chọn còn lại trong
// DEFAULT_SETTINGS (chụp 8 chọn 4, đóng dấu ngày) sẽ được thêm vào đây khi từng
// bước tương ứng hoàn thành — không bày ra công tắc bấm vào không có tác dụng gì.
// Khai báo ngoài component: nếu định nghĩa bên trong thì mỗi lần render React
// coi đây là kiểu component mới và dựng lại ô đánh dấu từ đầu, làm mất tiêu điểm
// bàn phím ngay sau khi bấm.

// Chip trong bảng cài đặt tự giãn đều nhau thành một dải liền như nút gạt phân
// đoạn (segmented control) — gọn và dễ bấm hơn trên điện thoại.
const Chip = ({ active, disabled, onClick, children }) => (
    <button
        type="button"
        aria-pressed={active}
        disabled={disabled}
        onClick={onClick}
        style={{
            ...(active ? { ...chip, ...chipActive } : chip),
            flex: 1,
            textAlign: "center",
            padding: "9px 12px",
            opacity: disabled ? 0.45 : 1,
            cursor: disabled ? "default" : "pointer"
        }}
    >
        {children}
    </button>
);

const Toggle = ({ checked, disabled, onChange, label }) => (
    <label style={{ ...toggleRow, opacity: disabled ? 0.45 : 1 }}>
        <span>{label}</span>
        <input
            type="checkbox"
            checked={checked}
            disabled={disabled}
            onChange={e => onChange(e.target.checked)}
            style={{ width: 20, height: 20, accentColor: "#ff7aa2", cursor: "inherit" }}
        />
    </label>
);

export default function CaptureSettings({ settings, onChange, disabled, stripSize }) {
    return (
        <details style={wrap}>
            <summary style={summary}>⚙️ Cài đặt chụp</summary>

            <div style={body}>
                <section style={sectionFirst}>
                    <div style={rowLabel}>Kiểu chụp</div>
                    <div style={rowControls}>
                        {SHOT_MODES.map(m => (
                            <Chip
                                key={m.id}
                                active={settings.shotMode === m.id}
                                disabled={disabled}
                                onClick={() => onChange("shotMode", m.id)}
                            >
                                {m.label}
                            </Chip>
                        ))}
                    </div>
                    <div style={hint}>
                        {settings.shotMode === "auto"
                            ? `Bấm một lần, máy tự chụp hết ${stripSize} tấm — chụp xong tấm này là đếm ngược lại ngay cho tấm sau.`
                            : "Mỗi tấm bấm một lần."}
                    </div>
                </section>

                <section style={section}>
                    <div style={rowLabel}>Đếm ngược</div>
                    <div style={rowControls}>
                        {COUNTDOWN_OPTIONS.map(n => (
                            <Chip
                                key={n}
                                active={settings.countdown === n}
                                disabled={disabled}
                                onClick={() => onChange("countdown", n)}
                            >
                                {n} giây
                            </Chip>
                        ))}
                    </div>
                    {settings.shotMode === "auto" && (
                        <div style={hint}>
                            Cũng là khoảng nghỉ giữa hai tấm. Cả dải mất khoảng{" "}
                            {settings.countdown * stripSize} giây.
                        </div>
                    )}
                </section>

                <section style={section}>
                    <Toggle
                        label="Đèn flash lúc chụp"
                        checked={settings.flash}
                        disabled={disabled}
                        onChange={v => onChange("flash", v)}
                    />
                    <Toggle
                        label="Tiếng bíp đếm ngược"
                        checked={settings.sound}
                        disabled={disabled}
                        onChange={v => onChange("sound", v)}
                    />
                    <Toggle
                        label="Lưới canh khung"
                        checked={settings.grid}
                        disabled={disabled}
                        onChange={v => onChange("grid", v)}
                    />
                    <div style={hint}>
                        Hiện đúng phần ảnh sẽ được giữ lại. Ô ảnh trên khung không cùng tỉ lệ
                        với webcam nên mép ngoài sẽ bị cắt — bật cái này để thấy trước.
                    </div>
                    <Toggle
                        label="Lật gương"
                        checked={settings.mirror}
                        disabled={disabled}
                        onChange={v => onChange("mirror", v)}
                    />
                    <div style={hint}>
                        Tắt gương để chữ trong nền đọc xuôi. Ảnh chụp ra theo đúng lựa chọn này.
                    </div>
                </section>

                {disabled && <div style={hint}>Đang chụp — dừng lại mới đổi được cài đặt.</div>}
            </div>
        </details>
    );
}

const wrap = {
    marginTop: 16,
    width: "100%",
    border: "2px solid #e2c8bf",
    borderRadius: 14,
    background: "white",
    padding: "10px 14px",
    boxSizing: "border-box"
};

const summary = {
    fontFamily: "Broline",
    fontSize: 18,
    color: "#8c5b4a",
    cursor: "pointer",
    listStyle: "revert",
    textAlign: "left",
    padding: "4px 0"
};

const body = {
    paddingTop: 6
};

// Nhóm đầu tiên nằm sát ngay dưới tiêu đề nên không cần vạch ngăn.
const sectionFirst = {
    paddingTop: 8
};

// Các nhóm sau cách nhau bằng một vạch mảnh cho dễ đọc theo cụm.
const section = {
    paddingTop: 14,
    marginTop: 14,
    borderTop: "1px solid #f0e2dc"
};

const rowLabel = {
    fontFamily: "Broline",
    fontSize: 16,
    color: "#8c5b4a",
    textAlign: "left",
    marginBottom: 8
};

const rowControls = {
    display: "flex",
    gap: 8
};

const toggleRow = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "9px 0",
    fontFamily: "Broline",
    fontSize: 16,
    color: "#8c5b4a",
    cursor: "pointer"
};

const hint = {
    marginTop: 8,
    fontFamily: "Broline",
    fontSize: 13,
    lineHeight: 1.45,
    textAlign: "left",
    color: "#b48a7c"
};
